import { Label, makeStyles, tokens } from "@fluentui/react-components";
import parse from "html-react-parser";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Activity } from "../../models/Activity";
import { Field, FieldValue } from "../../models/Field";
import { FieldTypes } from "../../models/FieldTypes";
import { VsaaTag } from "../../models/VsaaTag";
import { DisplayField } from "../DisplayField";

interface ActivityHistoryDiffCardProps {
  currentActivityVersion: Activity;
  previousActivity: Activity | undefined;
  activityFields: Field[];
}

const useStyles = makeStyles({
  statusChange: {
    fontWeight: tokens.fontWeightBold,
  },
  metadataField: {
    display: "inline-block",
    marginLeft: tokens.spacingHorizontalS,
  },
  metadataCategory: {
    marginTop: tokens.spacingVerticalS,
  },
});

const ActivityHistoryDiffCard = (props: ActivityHistoryDiffCardProps) => {
  const { currentActivityVersion, previousActivity, activityFields } = props;
  const { t } = useTranslation();
  const styles = useStyles();

  const addedFields: Record<string, FieldValue> = useMemo(
    () =>
      getAddedUpdatedTagFields(
        previousActivity?.fieldValues?.tags || {},
        currentActivityVersion?.fieldValues?.tags || {}
      ),
    [
      previousActivity?.fieldValues?.tags,
      currentActivityVersion?.fieldValues?.tags,
    ]
  );

  const removedFields: Record<string, FieldValue> = useMemo(
    () =>
      getRemovedTagFields(
        previousActivity?.fieldValues?.tags || {},
        currentActivityVersion?.fieldValues?.tags || {}
      ),
    [
      previousActivity?.fieldValues?.tags,
      currentActivityVersion?.fieldValues?.tags,
    ]
  );

  function getAddedUpdatedTagFields(
    originalFields: Record<string, FieldValue>,
    newFields: Record<string, FieldValue>
  ): Record<string, FieldValue> {
    const addedFields: Record<string, FieldValue> = {};

    // Check for removed keys
    for (const key in newFields) {
      if (!(key in originalFields)) {
        addedFields[key] = newFields[key];
        continue;
      }

      if (
        !Array.isArray(originalFields[key]) ||
        !Array.isArray(newFields[key])
      ) {
        continue;
      }

      const originalTags = originalFields[key] as VsaaTag[];
      const updatedTags = newFields[key] as VsaaTag[];

      const addedInUpdatedTags = updatedTags.filter(
        (updated) =>
          !originalTags.some((original) => original.id === updated.id)
      );

      addedFields[key] = addedInUpdatedTags;
    }

    return addedFields;
  }

  function getRemovedTagFields(
    originalFields: Record<string, FieldValue>,
    updatedFields: Record<string, FieldValue>
  ): Record<string, FieldValue> {
    const removedFields: Record<string, FieldValue> = {};

    for (const key in originalFields) {
      if (!(key in updatedFields)) {
        removedFields[key] = originalFields[key];
        continue;
      }

      if (
        !Array.isArray(originalFields[key]) ||
        !Array.isArray(updatedFields[key])
      ) {
        continue;
      }

      const originalTags = originalFields[key] as VsaaTag[];
      const updatedTags = updatedFields[key] as VsaaTag[];

      const notInUpdatedTags = originalTags.filter(
        (originalItem) =>
          !updatedTags.some((updatedItem) => updatedItem.id === originalItem.id)
      );

      removedFields[key] = notInUpdatedTags;
    }

    return removedFields;
  }

  return (
    <>
      {currentActivityVersion.fieldValues?.booleans.active !==
        previousActivity?.fieldValues?.booleans.active && (
        <div className={styles.statusChange}>
          {currentActivityVersion.fieldValues?.booleans.active
            ? t("activityWasActivated")
            : t("activityWasDeactivated")}
        </div>
      )}
      {currentActivityVersion.fieldValues?.booleans.archived !==
        previousActivity?.fieldValues?.booleans.archived && (
        <div className={styles.statusChange}>
          {currentActivityVersion.fieldValues?.booleans.archived
            ? t("activityWasArchived")
            : t("activityWasUnarchived")}
        </div>
      )}
      {currentActivityVersion.description?.serializedHtml !==
        previousActivity?.description?.serializedHtml && (
        <div>
          {" "}
          {parse(
            currentActivityVersion?.description?.serializedHtml ?? ""
          )}{" "}
        </div>
      )}
      {activityFields.map((field) => {
        const fieldWasAdded =
          addedFields &&
          field.uniqueName in addedFields &&
          (addedFields as Record<string, VsaaTag[]>)[field.uniqueName]?.length >
            0;
        const fieldWasRemoved =
          removedFields &&
          field.uniqueName in removedFields &&
          (removedFields as Record<string, VsaaTag[]>)[field.uniqueName]
            ?.length > 0;

        if (!fieldWasAdded && !fieldWasRemoved) {
          return null;
        }

        return (
          <div key={field.uniqueName} className={styles.metadataCategory}>
            <Label>{field.displayName}</Label>
            <div className={styles.metadataField}>
              <DisplayField
                type={FieldTypes.Tag}
                label={field.uniqueName}
                value={fieldWasAdded ? addedFields[field.uniqueName] : []}
                disableTextWrap={true}
              />
            </div>
            <div className={styles.metadataField}>
              <DisplayField
                type={FieldTypes.Tag}
                label={field.uniqueName}
                value={fieldWasRemoved ? removedFields[field.uniqueName] : []}
                removed={fieldWasRemoved}
                disableTextWrap={true}
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

export default ActivityHistoryDiffCard;
